import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import DishCard from '../components/DishCard';
import DishDetailModal from '../components/DishDetailModal';
import StarRating from '../components/StarRating';
import { StarIcon, HeartIcon, SearchIcon, AdjustmentsIcon, ArrowLeftIcon, PhoneIcon, LocationIcon, CalendarIcon } from '../components/icons';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useOrder } from '../context/OrderContext';
import { Dish, OrderItem, Pack } from '../types';
import PackCard from '../components/PackCard';
import ReservationModal from '../components/ReservationModal';
import { AnimatePresence } from 'framer-motion';

const RestaurantDetailScreen: React.FC<{
  restaurantId: string | undefined;
  onClose?: () => void;
}> = ({ restaurantId, onClose }) => {
  const context = useContext(AppContext);
  const view = context.view;
  const setView = context.setView;

  const { getRestaurantById, toggleRestaurantFavorite, getUserRestaurantRating, updateRestaurantRating, ensureDishesLoaded, ensurePacksLoaded } = useData();
  const { currentUser } = useAuth();
  const restaurant = getRestaurantById(restaurantId || '');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null);
  const [activeTab, setActiveTab] = useState<'menu' | 'packs'>(view?.params?.initialTab || 'menu');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const selectedDishIdFromUrl = view?.params?.dishId;
  const editOrderId = view?.params?.editOrderId;

  const { placedOrders, updateOrderItems } = useAllOrders();
  const { addToOrder } = useOrder();
  const orderToEdit = useMemo(() => {
    return editOrderId ? placedOrders.find(o => o.id === editOrderId) : null;
  }, [editOrderId, placedOrders]);

  const [editedItems, setEditedItems] = useState<OrderItem[]>(() => {
    return orderToEdit ? [...orderToEdit.items] : [];
  });

  const handleSaveModifications = async () => {
    if (!editOrderId || !orderToEdit) return;
    const newSubtotal = editedItems.reduce((sum, item) => {
        const itemBasePrice = item.type === 'pack' ? (item.pack?.packPrice || 0) : (item.dish?.price || 0);
        const extrasCost = (item.selectedExtras || []).reduce((eSum, extra) => eSum + extra.price, 0);
        return sum + (itemBasePrice + extrasCost) * item.quantity;
    }, 0);
    const newTotal = newSubtotal + orderToEdit.deliveryFee;
    await updateOrderItems(editOrderId, editedItems, newTotal);
    window.history.back();
  };

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    if (restaurantId) {
      ensureDishesLoaded(restaurantId);
      ensurePacksLoaded(restaurantId);
    }
  }, [restaurantId]);

  // Sync selected dish with URL parameter
  useEffect(() => {
    if (selectedDishIdFromUrl && restaurant?.menu) {
      const dish = restaurant.menu.flatMap(c => c.dishes).find(d => d.id === selectedDishIdFromUrl);
      if (dish) setSelectedDish(dish);
    }
  }, [selectedDishIdFromUrl, restaurant]);

  // Support for Android Hardware Back Button
  useEffect(() => {
    const handleHardwareBack = (e: Event) => {
      if (selectedDish) {
        console.log("🛡️ [RestaurantDetail] Intercepted Back Button - Closing DishDetailModal");
        setSelectedDish(null);
        e.preventDefault();
      }
    };

    window.addEventListener('yumii:hardwareBack', handleHardwareBack);
    return () => window.removeEventListener('yumii:hardwareBack', handleHardwareBack);
  }, [selectedDish]);

  if (!restaurant) return null;

  return (
    <>
      <div className="min-h-screen bg-white flex flex-col relative w-full overflow-hidden">
      {/* Scrollable Content */}
      <div 
        ref={scrollRef} 
        className="flex-1 overflow-y-auto pb-32 overscroll-y-auto touch-pan-y"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        
        {/* Curved Header Section */}
        <div className="relative h-[480px] w-full mb-12">
          {/* Main Image with Substrate (Less Curved) */}
          <div className="absolute inset-0 overflow-hidden rounded-b-[48px] shadow-[0_20px_50px_rgba(0,0,0,0.3)] z-0">
            <img src={restaurant.image} alt={restaurant.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
          </div>

          {/* Header Buttons */}
          <button 
            onClick={() => window.history.back()} 
            className="absolute top-12 left-6 bg-white/10 backdrop-blur-xl p-3.5 rounded-full text-white border border-white/20 z-20 active:scale-90 transition-all shadow-2xl"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>

          <button 
            onClick={() => toggleRestaurantFavorite(restaurant.id)}
            className="absolute top-12 right-6 bg-white/10 backdrop-blur-xl p-3.5 rounded-full border border-white/20 z-20 active:scale-90 transition-all shadow-2xl"
          >
            <HeartIcon className={`w-6 h-6 ${restaurant.isFavorite ? 'text-[#E31E24] fill-current' : 'text-[#E31E24]'}`} />
          </button>

          {/* Bottom Content Overlay */}
          <div className="absolute inset-0 flex flex-col justify-end text-white px-8 pb-16 z-10">
            <div className="flex justify-between items-center w-full mb-1">
              <h1 className="text-4xl font-black tracking-tighter drop-shadow-2xl italic uppercase text-left">{restaurant.name}</h1>
              
              {/* Yellow Rating Pill */}
              <div className="bg-[#FFC107] px-4 py-1.5 rounded-full shadow-[0_4px_12px_rgba(255,193,7,0.4)] flex items-center gap-1.5 border border-white/20">
                <StarIcon className="w-4 h-4 text-black fill-current" />
                <span className="text-lg font-black text-black">{restaurant.rating.toFixed(1)}</span>
              </div>
            </div>
            
            {restaurant.slogan && (
              <p className="text-[10px] font-black text-white/80 max-w-xs line-clamp-2 drop-shadow-md italic uppercase tracking-[0.2em] mb-1 text-left">
                {restaurant.slogan}
              </p>
            )}

            {restaurant.deliveryEnabled === false && (
              <div className="bg-red-600 text-white text-[8px] font-black px-3 py-1 rounded-full w-fit mb-2 shadow-lg border border-white/20 uppercase tracking-widest italic animate-pulse">
                À Emporter Uniquement
              </div>
            )}

            {restaurant.globalDiscount > 0 && (
              <div className="bg-[#E31E24] text-white text-[10px] font-black px-4 py-1.5 rounded-full w-fit mb-2 shadow-[0_4px_15px_rgba(227,30,36,0.3)] border border-white/20 uppercase tracking-[0.2em] italic flex items-center gap-2">
                PROMO -{restaurant.globalDiscount}% SUR TOUT
              </div>
            )}

            {!restaurant.isOnline && (
              <div className="bg-black text-white text-[10px] font-black px-4 py-1.5 rounded-full w-fit mb-2 shadow-[0_4px_15px_rgba(0,0,0,0.5)] border border-white/20 uppercase tracking-[0.2em] italic flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,1)]" />
                Établissement Fermé
              </div>
            )}

            {/* Compact Interactive Rating Section (All in one line) */}
            <div className="relative z-[60] pointer-events-auto mt-3 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-xl w-fit flex items-center gap-4">
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/50">Noter</span>
              <div className="flex items-center gap-2">
                <StarRating 
                  size={4.5} 
                  initialRating={getUserRestaurantRating(restaurant.id) || Math.round(restaurant.rating)} 
                  onRating={(newRating) => {
                    console.log("⭐ Clicked rating:", newRating);
                    updateRestaurantRating(restaurant.id, newRating);
                  }}
                  activeColor="text-yellow-400"
                  inactiveColor="text-white/20"
                />
                <span className="text-[9px] font-bold text-white/30 uppercase tracking-tighter border-l border-white/10 pl-2 ml-1">
                  {restaurant.ratingCount} avis
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute bottom-[-32px] right-8 flex gap-4 z-30 pointer-events-auto items-center">
            {restaurant.tableReservationEnabled && (
                <button 
                    onClick={() => setShowReservationModal(true)}
                    className="h-16 px-8 bg-[#E31E24] text-white rounded-full flex items-center justify-center gap-3 shadow-[0_15px_30px_-5px_rgba(227,30,36,0.3)] transition-all active:scale-95 border border-white/20"
                >
                    <CalendarIcon className="w-6 h-6" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] italic">Réserver</span>
                </button>
            )}
            <button 
                onClick={() => {
                  const query = encodeURIComponent(restaurant.address || restaurant.name);
                  window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
                }}
                className="w-16 h-16 bg-[#E31E24] text-white rounded-full flex items-center justify-center shadow-[0_15px_30px_-5px_rgba(227,30,36,0.3)] transition-all active:scale-95"
            >
                <LocationIcon className="w-8 h-8" />
            </button>
            <a 
                href={`tel:${restaurant.phone}`}
                className="w-16 h-16 bg-[#E31E24] text-white rounded-full flex items-center justify-center shadow-[0_15px_30px_-5px_rgba(227,30,36,0.3)] transition-all active:scale-95"
            >
                <PhoneIcon className="w-8 h-8" />
            </a>
          </div>
        </div>

        <div className="p-6">

            {/* Navigation Tabs - Matches Photo Pill Design */}
            {restaurant.packs && restaurant.packs.length > 0 && (
              <div className="flex bg-gray-100 p-1 rounded-full mb-10 max-w-sm mx-auto shadow-inner">
                <button 
                    onClick={() => setActiveTab('menu')} 
                    className={`flex-1 py-3.5 rounded-full text-sm font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'menu' ? 'bg-[#E31E24] text-white shadow-lg' : 'text-gray-400'}`}
                >
                    Menu
                </button>
                <button 
                    onClick={() => setActiveTab('packs')} 
                    className={`flex-1 py-3.5 rounded-full text-sm font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'packs' ? 'bg-[#E31E24] text-white shadow-lg' : 'text-gray-400'}`}
                >
                    Pack
                </button>
              </div>
            )}

              {activeTab === 'menu' ? (
                <>
                  {/* Categories Pills - Cleaner Design */}
                  <div className="flex gap-2.5 overflow-x-auto no-scrollbar py-2 mb-4">
                    <button 
                        onClick={() => setActiveCategory('All')} 
                        className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === 'All' ? 'bg-[#E31E24] text-white shadow-md' : 'bg-gray-100 text-gray-400'}`}
                    >
                        Tout
                    </button>
                    {restaurant.menu.map(c => (
                      <button 
                        key={c.category} 
                        onClick={() => setActiveCategory(c.category)} 
                        className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${activeCategory === c.category ? 'bg-[#E31E24] text-white shadow-md' : 'bg-gray-100 text-gray-400'}`}
                      >
                        {c.category}
                      </button>
                    ))}
                  </div>
                  {restaurant.menu.map(cat => (
                    (activeCategory === 'All' || activeCategory === cat.category) && (
                      <div key={cat.category} className="animate-fade-in">
                        <h3 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic mb-6 border-l-8 border-[#E31E24] pl-4">{cat.category}</h3>
                        <div className="flex gap-6 overflow-x-auto no-scrollbar pb-10">
                          {cat.dishes.map(d => (
                            <DishCard 
                                key={d.id} 
                                dish={d} 
                                restaurantName={restaurant.name} 
                                restaurantId={restaurant.id} 
                                globalDiscount={restaurant.globalDiscount}
                                onPress={(dish) => setSelectedDish(dish)} 
                                isOffline={!restaurant.isOnline}
                                editModeParams={editOrderId ? { items: editedItems, onUpdateItems: setEditedItems } : undefined} 
                            />
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </>
              ) : (
                <div className="space-y-8">
                  <h3 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic">Nouveaux Packs</h3>
                  <div className="flex gap-6 overflow-x-auto no-scrollbar pb-4">
                    {restaurant.packs?.filter(p => p.available !== false).map(p => (
                      <PackCard 
                        key={p.id} 
                        pack={p} 
                        restaurantName={restaurant.name}
                        onAddToCart={(pack) => addToOrder(pack, restaurant.name, restaurant.id)} 
                        isOffline={!restaurant.isOnline}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {editOrderId && orderToEdit && (
        <div className="fixed bottom-24 left-4 right-4 bg-white shadow-2xl rounded-3xl p-6 z-[60] border border-gray-100 pointer-events-auto">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xl font-black text-red-600 uppercase tracking-widest">Modification</span>
            <span className="text-lg font-black">{editedItems.reduce((s, i) => s + ((i.type === 'pack' ? (i.pack?.packPrice || 0) : (i.dish?.price || 0)) + (i.selectedExtras || []).reduce((es, e) => es + e.price, 0)) * i.quantity, 0) + orderToEdit.deliveryFee} DA</span>
          </div>
          <div className="flex gap-3">
            <button onClick={() => window.history.back()} className="flex-1 py-4 bg-gray-100 text-gray-900 font-black rounded-xl text-xs uppercase">Annuler</button>
            <button onClick={handleSaveModifications} className="flex-1 py-4 bg-red-600 text-white font-black rounded-xl text-xs uppercase">Valider</button>
          </div>
        </div>
      )}

      {selectedDish && (
        <div className="pointer-events-auto">
          <DishDetailModal 
            dish={selectedDish} 
            restaurantName={restaurant.name} 
            restaurantId={restaurant.id} 
            onClose={() => view.params?.dishId ? window.history.back() : setSelectedDish(null)} 
            isOffline={!restaurant.isOnline}
            globalDiscount={restaurant.globalDiscount}
            editModeParams={editOrderId ? { items: editedItems, onUpdateItems: setEditedItems } : undefined} 
          />
        </div>
      )}

      <AnimatePresence>
        {showReservationModal && (
            <ReservationModal 
                restaurant={restaurant} 
                onClose={() => setShowReservationModal(false)} 
            />
        )}
      </AnimatePresence>
    </>
  );
};

export default RestaurantDetailScreen;